import { api } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'
import type {
  ActivityRequestBody, ActivityResponse, AdoptionBatchResponse, AdoptionResponse,
  AssignmentResponse, CoverageResponse, CursorPage, HierarchyNodeResponse,
  LeaderboardResponse, MissionResponse, MobilizerAnalyticsResponse,
  MobilizerCompareResponse, MobilizerContactResponse, MobilizerDetailResponse,
  MobilizerMeResponse, MobilizerResponse, MobilizerStatisticsResponse,
  PerformanceRecomputeResponse, StationAdoptionResponse, SupervisorMoveResponse,
  TaskAssignResponse, TaskResponse, TerminationImpact,
} from '@/shared/api/types'

/**
 * The field network.
 *
 * ## Two rules govern almost every call in this file
 *
 * **1. A mobilizer's phone number never rides in a URL.** It is PII for the same
 * reason a voter's national ID is — a mobilizer contact list is a target list,
 * for poaching, for intimidation, for resale. So the phone search is
 * `POST /mobilizers/lookup` with the number in the body, matching the shape
 * `POST /transfers/lookup` already established, and `?q=` refuses a digit string
 * with `mobilizer/phone-in-query` rather than quietly searching names for it.
 * Nothing here ever puts a number in a query string, a route param or a fragment.
 *
 * **2. A mobilizer is a PERSON; `MOBILIZER` is a ROLE.** They are linked by a
 * nullable `userId` and they are not the same thing. Most mobilizers have no
 * login at all, and deleting a user must not delete the person's field history.
 * `linkUser`/`unlinkUser` are the only calls that touch the join.
 */

// ── the roster ──────────────────────────────────────────────────────────────

export const listMobilizers = (params: Record<string, QueryValue>, cursor?: string, size = 50) =>
  api.get<CursorPage<MobilizerResponse>>(`/mobilizers${qs({ ...params, cursor, size })}`)

export const mobilizerStatistics = (params: Record<string, QueryValue> = {}) =>
  api.get<MobilizerStatisticsResponse>(`/mobilizers/statistics${qs(params)}`)

/**
 * Phone lookup. The number goes in the BODY — see the note at the top of this
 * file. An unknown number is an empty page, not a 404: a 404 would answer a
 * question about whether a real person exists.
 */
export const lookupMobilizerByPhone = (
  phone: string, phoneCountry?: string, cursor?: string, size = 50,
) =>
  api.post<CursorPage<MobilizerResponse>>(
    '/mobilizers/lookup', { phone, phoneCountry, cursor, size })

export const getMobilizer = (id: string) =>
  api.get<MobilizerDetailResponse>(`/mobilizers/${id}`)

export const createMobilizer = (body: unknown) =>
  api.post<MobilizerDetailResponse>('/mobilizers', body)

/** `If-Match` carries the row version; a stale one is a 409, not a silent overwrite. */
export const updateMobilizer = (id: string, body: unknown, version: number) =>
  api.patch<MobilizerDetailResponse>(`/mobilizers/${id}`, body, {
    headers: { 'If-Match': String(version) },
  })

export const changeMobilizerStatus = (id: string, status: string, reason?: string) =>
  api.post<MobilizerDetailResponse>(`/mobilizers/${id}/status`, { status, reason })

/**
 * The full phone and national ID. Audited server-side, one row per call, naming
 * the caller and the subject — so this is called on an explicit click and the
 * result is never cached, never logged and never put into a list response.
 */
export const revealContact = (id: string) =>
  api.get<MobilizerContactResponse>(`/mobilizers/${id}/contact`)

// ── assignments ─────────────────────────────────────────────────────────────
// A mobilizer works in MANY units. Exactly one is primary, and deleting the
// primary is REFUSED rather than silently repaired — picking someone's home unit
// for them is not a decision the server is entitled to make.

export const addAssignment = (id: string, unitId: string, primary = false) =>
  api.post<AssignmentResponse>(`/mobilizers/${id}/assignments`, { unitId, primary })

export const removeAssignment = (id: string, unitId: string) =>
  api.del<AssignmentResponse>(`/mobilizers/${id}/assignments/${unitId}`)

export const makeAssignmentPrimary = (id: string, unitId: string) =>
  api.put<AssignmentResponse>(`/mobilizers/${id}/assignments/${unitId}/primary`)

// ── hierarchy ───────────────────────────────────────────────────────────────

/** One level per call. The whole tree is never fetched — a county has thousands. */
export const hierarchyLevel = (parentId?: string) =>
  api.get<HierarchyNodeResponse[]>(`/mobilizers/hierarchy${qs({ parentId })}`)

/**
 * Rewrites the moved person's whole subtree in one statement and returns how many
 * people that touched. The UI shows that number in the confirm dialog BEFORE the
 * call, which is why this is a deliberate action and not a drag gesture.
 * `supervisorId: null` promotes them to a root — legal, and what a national
 * director is.
 */
export const changeSupervisor = (id: string, supervisorId: string | null) =>
  api.put<SupervisorMoveResponse>(`/mobilizers/${id}/supervisor`, { supervisorId })

// ── the user link ───────────────────────────────────────────────────────────

export const linkUser = (id: string, userId: string) =>
  api.put<MobilizerDetailResponse>(`/mobilizers/${id}/user`, { userId })

export const unlinkUser = (id: string) =>
  api.del<MobilizerDetailResponse>(`/mobilizers/${id}/user`)

// ── termination ─────────────────────────────────────────────────────────────

/**
 * The dry run. Computed by the SAME method `terminate` uses, so the dialog cannot
 * promise one thing and the button do another. Writes nothing.
 */
export const terminationPreview = (id: string) =>
  api.get<TerminationImpact>(`/mobilizers/${id}/termination-preview`)

export const terminateMobilizer = (id: string, reason: string) =>
  api.post<TerminationImpact>(`/mobilizers/${id}/terminate`, { reason })

// ── adoption ────────────────────────────────────────────────────────────────
// Adoption is not assignment. Assignment says where someone WORKS; adoption says
// what they are ANSWERABLE for, and exactly one person is answerable at a time.

export const listAdoptions = (params: Record<string, QueryValue> = {}) =>
  api.get<AdoptionResponse[]>(`/adoptions${qs(params)}`)

/**
 * Bulk is the default shape, not a convenience — a ward coordinator adopts thirty
 * stations at once. Refusals come back as DATA, one result per station: the
 * twenty-nine that succeeded stay committed and the one that a colleague got to
 * first names them.
 */
export const adoptStations = (mobilizerId: string, stationIds: string[], note?: string) =>
  api.post<AdoptionBatchResponse>(`/mobilizers/${mobilizerId}/adoptions`, { stationIds, note })

export const releaseStation = (mobilizerId: string, stationId: string, reason?: string) =>
  api.del<AdoptionResponse>(`/mobilizers/${mobilizerId}/adoptions/${stationId}`, {
    body: { reason },
  })

/** Release-then-adopt, one transaction per station. An unadopted station is just adopted. */
export const reassignStations = (stationIds: string[], toMobilizerId: string, reason?: string) =>
  api.post<AdoptionBatchResponse>('/adoptions/reassign', { stationIds, toMobilizerId, reason })

/** Who owns this station now, and who owned it before — one call. */
export const stationAdoption = (stationId: string) =>
  api.get<StationAdoptionResponse>(`/stations/${stationId}/adoption`)

// ── coverage ────────────────────────────────────────────────────────────────

export const coverage = (params: Record<string, QueryValue> = {}) =>
  api.get<CoverageResponse>(`/mobilizers/coverage${qs(params)}`)

// ── activity ────────────────────────────────────────────────────────────────

/**
 * The feed. Keyset-paged, and the cursor is COMPOSITE — `occurredAt` is
 * client-supplied and backdatable by up to a week, so id order is not time order
 * and the bare-uuid cursor the rest of this app uses would skip rows. Treat it
 * as opaque; it is.
 *
 * `from`/`to` are ISO DATES, not instants: an activity at 01:00 in Nairobi
 * belongs to that day, and a laptop set to UTC would file it under the day before.
 */
export const activityFeed = (params: Record<string, QueryValue>, cursor?: string, size = 50) =>
  api.get<CursorPage<ActivityResponse>>(`/activities${qs({ ...params, cursor, size })}`)

/**
 * Record one activity for the CALLER — there is no `mobilizerId` in the body.
 *
 * The idempotency key is a REQUIRED header and the caller picks it BEFORE the
 * first attempt, not per attempt: a phone on one bar retries, and a key that
 * changes on retry is no key at all. A repeat returns 200 with the original row
 * rather than 201 or a 409, because the client cannot tell whether its first
 * attempt arrived and the only kind answer is the row it would have got.
 */
export const recordActivity = (body: ActivityRequestBody, idempotencyKey: string) =>
  api.post<ActivityResponse>('/activities', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })

// ── missions and tasks ──────────────────────────────────────────────────────

// `GET /mobilizers/missions/{id}`, `PATCH /mobilizers/missions/{id}`,
// `GET /mobilizers/tasks/{id}` and `GET /activities/{id}` all exist on the server
// and are deliberately NOT wrapped here: no screen fetches one of these on its
// own, every list already carries the full record, and an exported function
// nothing calls is a function nobody keeps correct. Add them back beside the
// screen that needs them.

export const listMissions = (params: Record<string, QueryValue> = {}) =>
  api.get<MissionResponse[]>(`/mobilizers/missions${qs(params)}`)

export const createMission = (body: unknown) =>
  api.post<MissionResponse>('/mobilizers/missions', body)

export const changeMissionStatus = (id: string, status: string) =>
  api.post<MissionResponse>(`/mobilizers/missions/${id}/status`, { status })

/**
 * Bulk assignment is idempotent by constraint — running it twice does not double
 * anybody's workload. `alreadyAssigned` is how many were skipped, and it is
 * reported rather than hidden, because "created 0" with no explanation reads as a
 * failure.
 */
export const assignTasks = (missionId: string, body: unknown) =>
  api.post<TaskAssignResponse>(`/mobilizers/missions/${missionId}/tasks`, body)

export const listTasks = (params: Record<string, QueryValue> = {}) =>
  api.get<TaskResponse[]>(`/mobilizers/tasks${qs(params)}`)

export const acceptTask = (id: string) =>
  api.post<TaskResponse>(`/mobilizers/tasks/${id}/accept`)

export const startTask = (id: string) =>
  api.post<TaskResponse>(`/mobilizers/tasks/${id}/start`)

/**
 * Completing is a HUMAN act. Reaching the target does not complete a task, and a
 * task may be completed under target — "14/20, the rest have moved away" is real
 * information a hard gate would destroy.
 */
export const completeTask = (id: string, outcomeNote?: string) =>
  api.post<TaskResponse>(`/mobilizers/tasks/${id}/complete`, { outcomeNote })

export const cancelTask = (id: string, outcomeNote?: string) =>
  api.post<TaskResponse>(`/mobilizers/tasks/${id}/cancel`, { outcomeNote })

// ── scoring ─────────────────────────────────────────────────────────────────

/**
 * Read from the nightly rollup, never computed live. Every response carries
 * `computedAt` and `rulesVersion`, and the screens print both — users otherwise
 * assume it is live and report the lag as a bug.
 */
export const leaderboard = (params: Record<string, QueryValue> = {}) =>
  api.get<LeaderboardResponse>(`/mobilizers/leaderboard${qs(params)}`)

export const analytics = (params: Record<string, QueryValue> = {}) =>
  api.get<MobilizerAnalyticsResponse>(`/mobilizers/analytics${qs(params)}`)

/** Two to five. One is the detail page; six is a table. */
export const compareMobilizers = (mobilizerIds: string[], from?: string, to?: string) =>
  api.post<MobilizerCompareResponse>('/mobilizers/compare', { mobilizerIds, from, to })

/**
 * Recomputing day D also recomputes the `window-days − 1` days after it, because
 * each of those windows contained D. The response says which `effectiveTo` it
 * actually used — do not print the requested `to` as if it were the whole job.
 */
export const recomputePerformance = (from: string, to: string) =>
  api.post<PerformanceRecomputeResponse>('/mobilizers/performance/recompute', { from, to })

// ── the mobilizer's own screen ──────────────────────────────────────────────

/**
 * Always a 200, never a 403. A campaign manager opening the field screen is not
 * an error — they simply have no field identity, and `isMobilizer: false` says
 * so with a body the client can parse.
 */
export const myFieldProfile = () => api.get<MobilizerMeResponse>('/mobilizers/me')
